<template>
  <div class="card-item min-height-100">
    <section v-if="!isSummaryVisible">
      <div class="mb-4">
        <p class="is-size-6">{{ $t('treasury.selectFileType') }}:</p>
      </div>
      <div class="columns">
        <div class="column">
          <b-dropdown
            v-model="selectedOption"
            :mobile-modal="false"
            expanded
            append-to-body
            aria-role="list">
            <b-button id="dropdown-button" slot="trigger" slot-scope="{ active }" class="button" expanded>
              <span>{{ selectedOption.name }}</span>
              <b-icon :icon="active ? 'angle-up' : 'angle-down'" />
            </b-button>
            <b-dropdown-item
              v-for="option in options"
              :id="option.id"
              :key="option.id"
              :value="option"
              aria-role="listitem"
              @change="refresh()">
              {{ option.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="column">
          <section>
            <b-field>
              <b-upload
                id="upload-bank-entries"
                v-model="uploadedFile"
                expanded
                :disabled="!checkUserFeature('element.cash-flows.bank-entries.button.upload') || selectedOption.value === null || isProcessingFile"
                @input="processFile">
                <a class="button is-primary is-fullwidth">
                  <div v-if="!isProcessingFile">
                    <b-icon icon="upload" />
                    <span>{{ uploadedFile ? uploadedFile.name : uploadInstructions }}</span>
                  </div>
                  <span v-else>
                    <i class="fas fa-spinner fa-spin" />
                    <span class="pl-3">{{ `Procesando archivo ${uploadedFile.name}` }}</span>
                  </span>
                </a>
              </b-upload>
            </b-field>
            <p v-if="!uploadedFile" class="is-size-7 has-text-grey" />
            <p v-else-if="!isFileExtensionValid" class="is-size-7 has-text-danger">
              {{ uploadInstructions }}
            </p>
            <p v-else-if="fileContentErrorMessage !== null" id="error-message" class="is-size-7 has-text-danger">
              {{ fileContentErrorMessage }}
            </p>
            <div v-if="selectedOption.value === 'BANK_DEPOSITS_CL_BCI'" class="is-size-7 has-text-grey">
              <a id="download-deposits-template" expanded @click="createDepositsWorkbook">
                <b-icon icon="download" />
                <u><span class="pl-2">Descargar template Depósitos</span></u>
              </a>
            </div>
          </section>
        </div>
      </div>
      <div class="control has-text-right">
        <button
          id="create-bank-entries"
          :disabled="!isFileContentValid"
          class="button is-button-primary"
          @click="createBankEntries">
          <span v-if="isCreating">
            <i class="fas fa-spinner fa-spin" />
          </span>
          <span v-else>{{ $t('generalWords.Upload') }}</span>
        </button>
      </div>
    </section>
    <section v-else>
      <div class="control has-text-centered">
        <Message
          v-if="errorMessageOnCreating !== null"
          id="error-message"
          :message="errorMessageOnCreating.message"
          :icon="errorMessageOnCreating.icon"
          :as-card="false" />
        <div v-else>
          <p class="has-text-left is-size-6">{{ $t('treasury.bankEntriesUploadResults') }}:</p>
          <b-table
            v-if="bankEntriesResponse.errors.length > 0"
            hoverable
            :data="bankEntriesResponse.errors">
            <b-table-column v-slot="bankEntryResponseError" field="errors" :label="$t('generalWords.Errors')">
              <span class="tag is-primary is-alert is-size-6">{{ bankEntryResponseError.row.message }}</span>
            </b-table-column>
          </b-table>
          <b-table
            hoverable
            :data="bankEntriesResponseTable">
            <b-table-column v-slot="bankEntryResponseTable" field="bankEntryId" :label="$t('treasury.bankTransactionNumberLabel')" :centered="true">
              <span v-if="bankEntryResponseTable.row.bankEntryId">
                {{ bankEntryResponseTable.row.bankEntryId }}
              </span>
            </b-table-column>
            <b-table-column v-slot="bankEntryResponseTable" field="idNumber" :label="$t('treasury.originIdLabel')" :centered="true">
              {{ bankEntryResponseTable.row.idNumber }}
            </b-table-column>
            <b-table-column v-slot="bankEntryResponseTable" field="date" :label="$t('generalWords.Date')" :centered="true">
              {{ bankEntryResponseTable.row.date }}
            </b-table-column>
            <b-table-column v-slot="bankEntryResponseTable" field="accountingDate" :label="$t('treasury.accountableDateLabel')" :centered="true">
              {{ bankEntryResponseTable.row.accountingDate }}
            </b-table-column>
            <b-table-column v-slot="bankEntryResponseTable" field="amount" :label="$t('generalWords.Amount')" :centered="true">
              <span v-if="bankEntryResponseTable.row.amount" class="tag is-primary is-light is-size-6">
                {{ formatNumberByCountry(bankEntryResponseTable.row.amount) }}
              </span>
            </b-table-column>
            <b-table-column v-slot="bankEntryResponseTable" field="status" label="Status" :centered="true">
              <span v-if="bankEntryResponseTable.row.status" class="tag is-primary is-light is-size-6">
                {{ bankEntryResponseTable.row.status }}
              </span>
            </b-table-column>
          </b-table>
        </div>
        <button
          id="refresh-file-uploading"
          class="button is-button-primary"
          @click="refresh(true)">
          <span>Ok</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import notification from '@/ui/mixins/notification';
import { createBankEntries } from '@/application/services/api-clients/bank-entries';
import * as xlsx from 'xlsx';
import { BankEntriesReader } from '@/application/services/readers/bank-entries-reader';
import { BankEntriesTransformer } from '@/application/services/readers/bank-entries-transformer';
import userFeatures from '@/ui/mixins/user-features';
import { formatNumberByCountry } from '@/infrastructure/format';
import Message from '@/ui/components/Message.vue';

export default {
  name: 'BankEntries',
  components: { Message },
  mixins: [notification, userFeatures],
  data() {
    return {
      sources: {
        BANK_STATEMENT_CL_BCI: 'BANK_STATEMENT_CL_BCI',
        BANK_TRANSFERS_REPORT_CL_BCI: 'BANK_TRANSFERS_REPORT_CL_BCI',
        BANK_DEPOSITS_CL_BCI: 'BANK_DEPOSITS_CL_BCI',
        BANK_STATEMENT_BR_ITAU_XLSX: 'BANK_STATEMENT_BR_ITAU_XLSX',
        BANK_STATEMENT_BR_ITAU_TXT: 'BANK_STATEMENT_BR_ITAU_TXT',
        BANK_STATEMENT_BR_SANTANDER: 'BANK_STATEMENT_BR_SANTANDER',
        BANK_STATEMENT_PE_TXT: 'BANK_STATEMENT_PE_TXT'
      },
      options: {
        br: [
          {
            name: 'Extracto Bancario ITAU [xlsx]',
            value: 'BANK_STATEMENT_BR_ITAU_XLSX',
            id: 'bank-statement-br-itau-xlsx'
          },
          {
            name: 'Extracto Bancario ITAU [txt]',
            value: 'BANK_STATEMENT_BR_ITAU_TXT',
            id: 'bank-statement-br-itau-txt'
          },
          {
            name: 'Extracto Bancario Santander',
            value: 'BANK_STATEMENT_BR_SANTANDER',
            id: 'bank-statement-br-santander'
          }
        ],
        cl: [
          {
            name: 'Transferencias Recibidas',
            value: 'BANK_TRANSFERS_REPORT_CL_BCI',
            id: 'bank-transfers-report-cl-bci'
          },
          {
            name: 'Cartola',
            value: 'BANK_STATEMENT_CL_BCI',
            id: 'bank-statement-cl-bci'
          },
          {
            name: 'Depósitos en efectivo',
            value: 'BANK_DEPOSITS_CL_BCI',
            id: 'bank-deposits-cl-bci'
          }
        ],
        pe: [
          {
            name: 'Informe de Pagos',
            value: 'BANK_STATEMENT_PE_TXT',
            id: 'bank-statement-pe-txt'
          }
        ]
      }[this.$i18n.locale],
      selectedOption: {
        name: { br: 'Selecione uma opção', cl: 'Selecciona una opción', pe: 'Selecciona una opción' }[this.$i18n.locale],
        value: null
      },
      uploadedFile: null,
      bankEntriesToCreate: null,
      bankEntriesResponse: null,
      isProcessingFile: false,
      fileContentErrorMessage: null,
      isCreating: false,
      isSummaryVisible: false,
      errorMessageOnCreating: null
    };
  },
  computed: {
    uploadInstructions() {
      if (!this.selectedOption.value) {
        return 'Primero selecciona una opción';
      }
      return {
        br: {
          BANK_STATEMENT_BR_ITAU_XLSX: 'Você deve fazer upload de um documento do Excel [.xlsx]',
          BANK_STATEMENT_BR_ITAU_TXT: 'Você deve fazer upload de um documento do Excel [.txt]',
          BANK_STATEMENT_BR_SANTANDER: 'Você deve fazer upload de um documento do Excel [.xlsx]'
        },
        cl: {
          BANK_TRANSFERS_REPORT_CL_BCI: 'Sube un archivo excel [.xls o .xlsx]',
          BANK_STATEMENT_CL_BCI: 'Sube un archivo excel [.xls o .xlsx]',
          BANK_DEPOSITS_CL_BCI: 'Sube un archivo excel [.xls o .xlsx]'
        },
        pe: {
          BANK_STATEMENT_PE_TXT: 'Sube un documento de texto plano [.txt]'
        }
      }[this.$i18n.locale][this.selectedOption.value];
    },
    fileType() {
      if (this.uploadedFile !== null) {
        return this.uploadedFile.type;
      }
      return null;
    },
    isFileExtensionValid() {
      if (!this.uploadedFile) {
        return false;
      }
      const validationMap = {
        br: {
          BANK_STATEMENT_BR_ITAU_XLSX: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
          BANK_STATEMENT_BR_ITAU_TXT: ['text/plain'],
          BANK_STATEMENT_BR_SANTANDER: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
        }[this.selectedOption.value],
        cl: {
          BANK_STATEMENT_CL_BCI: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
          BANK_TRANSFERS_REPORT_CL_BCI: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
          BANK_DEPOSITS_CL_BCI: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
        }[this.selectedOption.value],
        pe: {
          BANK_STATEMENT_PE_TXT: ['text/plain']
        }[this.selectedOption.value]
      }[this.$i18n.locale];
      return validationMap.includes(this.fileType);
    },
    isFileContentValid() {
      return !this.isCreating && this.isFileExtensionValid && this.bankEntriesToCreate !== null && this.fileContentErrorMessage === null;
    },
    bankEntriesResponseTable() {
      if (!this.bankEntriesResponse) {
        return null;
      }
      const {
        bankEntriesWithoutChanges,
        createdBankEntries,
        updatedBankEntries,
        processBankEntriesResults
      } = this.bankEntriesResponse;
      const bankEntriesResponseTable = Object
        .entries({ bankEntriesWithoutChanges, createdBankEntries, updatedBankEntries })
        .reduce((acc, [bankEntryResponseTableKey, bankEntryResponseTableValues]) => {
          (bankEntryResponseTableValues || []).forEach(bankEntryResponseTableValue => {
            const processResult = (processBankEntriesResults || []).reduce((accum, processBankEntriesResult) => {
              return bankEntryResponseTableKey === 'createdBankEntries' && bankEntryResponseTableValue._id === processBankEntriesResult.bankEntryId
                ? processBankEntriesResult
                : accum;
            }, null);
            const bankEntryId = bankEntryResponseTableValue.bankTransactionNumber
              ? bankEntryResponseTableValue.bankTransactionNumber
              : (bankEntryResponseTableValue.bankEntryId || bankEntryResponseTableValue._id);
            const bankEntryIdType = bankEntryResponseTableValue.bankTransactionNumber ? 'id bancario' : 'id database';
            const amount = bankEntryResponseTableKey === 'processBankEntriesResults' ? bankEntryResponseTableValue.receivedAmount : bankEntryResponseTableValue.amount;

            const newBankEntryResult = {
              bankEntryId,
              bankEntryIdType,
              amount,
              idNumber: bankEntryResponseTableValue.entity.idNumber,
              date: bankEntryResponseTableValue.date,
              accountingDate: bankEntryResponseTableValue.accountingDate,
              processResult,
              status: {
                bankEntriesWithoutChanges: this.$t('generalWords.NoChanges'),
                createdBankEntries: this.$t('generalWords.Created'),
                updatedBankEntries: this.$t('generalWords.Updated'),
                processBankEntriesResults: this.$t('generalWords.Processed'),
                errors: this.$t('generalWords.Error')
              }[bankEntryResponseTableKey],
              message: bankEntryResponseTableKey.message || null
            };
            acc.push(newBankEntryResult);
          });
          return acc;
        }, []);
      return bankEntriesResponseTable;
    }
  },
  methods: {
    async processFile() {
      if (!this.isFileExtensionValid) {
        return;
      }
      this.isProcessingFile = true;
      this.fileContentErrorMessage = null;
      try {
        const reader = new BankEntriesReader(this.uploadedFile, this.$i18n.locale, xlsx, this.fileType);
        const rows = await reader.rows();
        let bankEntriesInputData;
        if (this.$i18n.locale === 'pe' && this.fileType === 'text/plain') {
          bankEntriesInputData = rows;
        } else {
          const transformer = new BankEntriesTransformer({
            rows,
            country: this.$i18n.locale,
            selectedOption: this.selectedOption.value
          });
          bankEntriesInputData = transformer.translateJsonToBankEntriesInput();
        }
        this.bankEntriesToCreate = {
          bankEntriesSource: this.selectedOption.value,
          bankEntriesInputData
        };
      } catch (_error) {
        this.fileContentErrorMessage = _error.message;
      }
      this.isProcessingFile = false;
    },
    async createBankEntries() {
      if (!this.bankEntriesToCreate) {
        return;
      }
      this.isCreating = true;
      try {
        this.bankEntriesResponse = await createBankEntries(this.bankEntriesToCreate);
      } catch (_error) {
        this.errorMessageOnCreating = {
          message: this.$t('treasury.uploadFileError'),
          icon: 'data_maintenance.svg'
        };
      }
      this.isSummaryVisible = true;
      this.isCreating = false;
    },
    formatNumberByCountry(number) {
      const fractionDigits = {
        br: 2,
        cl: 0,
        pe: 2
      }[this.$i18n.locale];
      return formatNumberByCountry({ number, countryIn: this.$i18n.locale, usePrefix: true, fractionDigits, currencyType: 'secondary' });
    },
    refresh(hard = false) {
      this.uploadedFile = null;
      this.isSummaryVisible = false;
      this.errorMessageOnCreating = null;
      if (hard) {
        this.selectedOption = {
          name: { br: 'Selecione uma opção', cl: 'Selecciona una opción', pe: 'Selecciona una opción' }[this.$i18n.locale],
          value: null
        };
        this.bankEntriesResponse = null;
      }
    },
    createDepositsWorkbook() {
      const headers = [['Fecha [YYYY-MM-DD]', 'Monto [123456]', 'Serial', 'Nombre', 'RUT']];
      const workbook = xlsx.utils.book_new();
      const worksheet = xlsx.utils.json_to_sheet([]);
      xlsx.utils.sheet_add_aoa(worksheet, headers);
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Depósitos');
      xlsx.writeFile(workbook, 'Depósitos Bancarios BCI.xlsx');
    }
  }
};
</script>

<style scoped>
.min-height-100 {
  min-height: 100px !important;
}

</style>
